import { z } from 'zod';

/**
 * Email hygiene helpers for signup: normalization, format validation,
 * and typo suggestions for common providers / TLDs.
 * Purely local — no network calls, no third-party service.
 */

const emailSchema = z.string().trim().min(1).max(255).email();

/** Trim, lowercase, strip stray whitespace and trailing punctuation. */
export function normalizeEmail(input: string): string {
  return (input || '')
    .replace(/\s+/g, '')
    .replace(/[.,;]+$/, '')
    .toLowerCase();
}

export function validateEmailFormat(input: string): { valid: boolean; error?: string } {
  const value = normalizeEmail(input);
  if (!value) return { valid: false, error: 'Please enter your email address' };
  const result = emailSchema.safeParse(value);
  if (!result.success) {
    return { valid: false, error: "That doesn't look like a valid email address" };
  }
  return { valid: true };
}

const COMMON_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'ymail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'bellsouth.net',
  'charter.net',
  'cox.net',
];

const COMMON_TLDS = ['com', 'net', 'org', 'edu', 'gov', 'co.uk', 'ca', 'io', 'me'];

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = curr;
  }
  return prev[n];
}

/**
 * Suggest a corrected email when the domain looks like a near-miss of a
 * common provider, or the TLD looks mistyped (.con, .cmo, .comm ...).
 * Returns null when nothing obvious is wrong.
 */
export function suggestEmailCorrection(input: string): string | null {
  const value = normalizeEmail(input);
  const at = value.lastIndexOf('@');
  if (at <= 0 || at === value.length - 1) return null;

  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (!domain.includes('.')) return null;

  // Exact match on a known domain — nothing to fix.
  if (COMMON_DOMAINS.includes(domain)) return null;

  // 1. Near-miss on a full known domain.
  let best: { domain: string; distance: number } | null = null;
  for (const candidate of COMMON_DOMAINS) {
    const distance = levenshtein(domain, candidate);
    const limit = candidate.length <= 6 ? 1 : 2;
    if (distance <= limit && (!best || distance < best.distance)) {
      best = { domain: candidate, distance };
    }
  }
  if (best) return `${local}@${best.domain}`;

  // 2. Near-miss on the TLD only (e.g. mycompany.con).
  const lastDot = domain.lastIndexOf('.');
  const base = domain.slice(0, lastDot);
  const tld = domain.slice(lastDot + 1);
  if (COMMON_TLDS.includes(tld)) return null;
  for (const candidate of COMMON_TLDS) {
    if (candidate.includes('.')) continue;
    if (levenshtein(tld, candidate) <= 1 && tld !== candidate) {
      return `${local}@${base}.${candidate}`;
    }
  }

  return null;
}
